import { useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./recorder.css";
import ChunkIndicator from "./ChunkIndicator";
import WorkspacePanel from "./WorkspacePanel";

const WORKSPACE_TABS = [
  { id: "raw", label: "Raw" },
  { id: "structured", label: "Structured" },
];

const MicIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="9" y="2" width="6" height="12" rx="3" fill="currentColor" />
    <path
      d="M5 11a7 7 0 0 0 14 0M12 18v3"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

/**
 * A fully-visible curved wire drawn on a fixed layer (portaled to <body>) so it
 * is never clipped by the toolbar or the timeline section's overflow. It links
 * the Recorder button (`.rec-mic-pill`) to the top of the suggestions overlay.
 */
function RecorderWire({ anchorRef }) {
  const [geo, setGeo] = useState(null);

  useLayoutEffect(() => {
    const update = () => {
      const btn = document.querySelector(".rec-mic-pill");
      const target = anchorRef.current;
      if (!btn || !target) {
        setGeo(null);
        return;
      }
      const b = btn.getBoundingClientRect();
      const t = target.getBoundingClientRect();
      setGeo({
        x1: b.left + b.width / 2,
        y1: b.bottom,
        x2: t.left + t.width / 2,
        y2: t.top,
      });
    };

    update();
    const id = window.setInterval(update, 200);
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.clearInterval(id);
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [anchorRef]);

  if (!geo) return null;

  const midY = (geo.y1 + geo.y2) / 2;
  const d = `M ${geo.x1} ${geo.y1} C ${geo.x1} ${midY}, ${geo.x2} ${midY}, ${geo.x2} ${geo.y2}`;

  return createPortal(
    <svg className="rec-wire" aria-hidden="true">
      <path className="rec-wire__path" d={d} />
      <circle className="rec-wire__dot" cx={geo.x1} cy={geo.y1} r="4" />
      <path
        className="rec-wire__head"
        d={`M ${geo.x2 - 5} ${geo.y2 - 8} L ${geo.x2} ${geo.y2} L ${geo.x2 + 5} ${geo.y2 - 8} Z`}
      />
    </svg>,
    document.body,
  );
}

/**
 * Overlays the whole timeline section with the prototype's Transcript panel
 * (Raw / Revised / Structured). Same chunking behaviour as
 * ai-medical-consultation-assistant: the ChunkIndicator shows "Chunk #N sent".
 *
 * Closing collapses to a small mic circle; leaving the consultation unmounts.
 * "+ Add" (Structured tab) also inserts the text, as words, into the
 * examination editor.
 */
export default function RecorderSuggestionsPanel({ recorder, onInsert, readOnly }) {
  const overlayRef = useRef(null);
  const [workspaceTab, setWorkspaceTab] = useState("structured");

  if (!recorder) return null;

  const {
    isRecording,
    isAnalyzing,
    chunkCount,
    error,
    panelOpen,
    setPanelOpen,
    sessionActive,
    toggleRecording,
    transcriptLines,
    revisedLines,
    propositions,
    onEditPropositionItem,
    onAddPropositionItem,
    onAddAllPropositions,
  } = recorder;

  const observations = propositions.observations ?? [];
  const pendingCount = observations.length;
  // Once a recording session has started, the panel (and its reopen circle)
  // stay available for the whole appointment — until the consultation is left.
  const hasContent =
    sessionActive || isRecording || isAnalyzing || observations.length > 0 || !!error;

  // Adding a suggestion inserts it into the examination editor (and persists it)
  // and flags it as "added" so the button turns green. It is not locked
  // afterwards, so the same suggestion can be added several times. Each value
  // simply goes on its own new line (single line break, no blank line).
  const handleAddProposition = (category, item) => {
    if (readOnly) return;
    onInsert?.({ item: item.text, leadingBreaks: 0 });
    if (!item.added) onAddPropositionItem?.(category ?? "observations", item);
  };

  const handleAddAll = (category) => {
    if (readOnly) return;
    (propositions[category] ?? []).forEach((i) =>
      onInsert?.({ item: i.text, leadingBreaks: 0 }),
    );
    onAddAllPropositions?.(category);
  };

  // Full AI-revised transcript, offered as one more suggestion at the bottom of
  // the Structured list (replaces the prototype's "Include revised transcript"
  // checkbox — here it is added, edited-first, like any other suggestion).
  const revisedTranscript = revisedLines.map((l) => l.text).join(" ").trim();

  const handleAddTranscript = (text) => {
    // Leave two blank lines before the transcript block, then the content
    // (no "- " bullet prefix).
    if (!readOnly) onInsert?.({ item: text, block: true, leadingBreaks: 2 });
  };

  if (!hasContent) return null;

  if (!panelOpen) {
    return (
      <button
        type="button"
        className={`rec-reopen ${isRecording ? "rec-reopen--live" : ""}`}
        onClick={() => setPanelOpen(true)}
        title="Ouvrir l'assistant"
        aria-label="Ouvrir l'assistant"
      >
        <MicIcon size={12} />
        {pendingCount > 0 && <span className="rec-reopen__count">{pendingCount}</span>}
      </button>
    );
  }

  return (
    <div className="rec-overlay" ref={overlayRef}>
      {/* the connector wire shows only while recording — it disappears on stop */}
      {isRecording && <RecorderWire anchorRef={overlayRef} />}

      <div className="rec-shell">
        <div className="rec-shell__bar">
          <div className="rec-shell__status">
            <ChunkIndicator chunkCount={chunkCount} isRecording={isRecording} />
            {isAnalyzing && <span className="rec-shell__meta">Analyzing…</span>}
          </div>
          <div className="rec-shell__controls">
            <div className="workspace-tabs" role="tablist">
              {WORKSPACE_TABS.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  role="tab"
                  aria-selected={workspaceTab === t.id}
                  className={`workspace-tab ${workspaceTab === t.id ? "is-active" : ""}`}
                  onClick={() => setWorkspaceTab(t.id)}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <button
              type="button"
              className={`rec-icon-btn rec-icon-btn--mic ${isRecording ? "is-live" : ""}`}
              onClick={toggleRecording}
              disabled={readOnly}
              title={isRecording ? "Arrêter l'enregistrement" : "Démarrer l'enregistrement"}
              aria-label={isRecording ? "Arrêter l'enregistrement" : "Démarrer l'enregistrement"}
            >
              <MicIcon />
            </button>
            <button
              type="button"
              className="rec-icon-btn"
              onClick={() => setPanelOpen(false)}
              title="Fermer"
              aria-label="Fermer l'assistant"
            >
              ✕
            </button>
          </div>
        </div>

        {error && <p className="rec-shell__error">{error}</p>}

        <div className="rec-stack">
          <WorkspacePanel
            tab={workspaceTab}
            rawLines={transcriptLines}
            revisedLines={revisedLines}
            propositions={propositions}
            onEditPropositionItem={onEditPropositionItem}
            onAddPropositionItem={handleAddProposition}
            onAddAllPropositions={handleAddAll}
            revisedTranscript={revisedTranscript}
            onAddTranscript={handleAddTranscript}
          />
        </div>
      </div>
    </div>
  );
}
